import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';
type TooltipTone = 'default' | 'info' | 'warning' | 'danger';

interface ReportTooltipProps {
  label?: React.ReactNode;
  title?: React.ReactNode;
  detail?: React.ReactNode | React.ReactNode[];
  meta?: React.ReactNode;
  tone?: TooltipTone;
  children: React.ReactElement;
  placement?: TooltipPlacement;
  maxW?: string;
}

const toneColor: Record<TooltipTone, string> = {
  default: '#64748b',
  info: '#2563eb',
  warning: '#d97706',
  danger: '#dc2626',
};

const renderTooltipContent = (
  title: React.ReactNode,
  detail: React.ReactNode | React.ReactNode[] | undefined,
  meta: React.ReactNode,
  label: React.ReactNode,
  tone: TooltipTone,
): React.ReactNode => {
  if (!title && !detail && !meta) return label;
  const details = Array.isArray(detail) ? detail.filter(Boolean) : detail ? [detail] : [];
  return (
    <span style={{ display: 'grid', gridTemplateColumns: '2px 1fr', columnGap: '6px', alignItems: 'start' }}>
      <span aria-hidden="true" style={{ width: '2px', height: '100%', minHeight: '20px', borderRadius: '999px', background: toneColor[tone] }} />
      <span style={{ display: 'grid', gap: '2px' }}>
        {title ? <span style={{ color: '#111827', fontWeight: 650 }}>{title}</span> : null}
        {details.map((line, index) => (
          <span key={index} style={{ color: '#64748b', fontWeight: 500 }}>{line}</span>
        ))}
        {meta ? <span style={{ color: '#94a3b8', fontSize: '9px', fontWeight: 600, textTransform: 'uppercase' }}>{meta}</span> : null}
      </span>
    </span>
  );
};

export const ReportTooltip: React.FC<ReportTooltipProps> = ({
  label,
  title,
  detail,
  meta,
  tone = 'default',
  children,
  placement = 'top',
  maxW = '240px',
}) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const hoverTimerRef = useRef<number | null>(null);
  const touchTimerRef = useRef<number | null>(null);
  const tooltipId = useMemo(() => `sr-tip-${Math.random().toString(36).slice(2)}`, []);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    placement: TooltipPlacement;
    arrowStyle: React.CSSProperties;
  } | null>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !panelRef.current) return;

    const margin = 8;
    const gap = 8;
    const arrowSize = 6;
    const trigger = triggerRef.current.getBoundingClientRect();
    const panel = panelRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const centerX = trigger.left + trigger.width / 2;
    const centerY = trigger.top + trigger.height / 2;
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));
    const fits = {
      top: trigger.top - panel.height - gap >= margin,
      bottom: trigger.bottom + panel.height + gap <= viewportH - margin,
      left: trigger.left - panel.width - gap >= margin,
      right: trigger.right + panel.width + gap <= viewportW - margin,
    };
    const preferred = [placement, placement === 'top' ? 'bottom' : 'top', 'right', 'left'] as const;
    const actual = preferred.find(side => fits[side]) || (['bottom', 'top', 'right', 'left'] as const).find(side => fits[side]) || placement;

    let top = 0;
    let left = 0;
    let arrowStyle: React.CSSProperties = {};

    if (actual === 'top' || actual === 'bottom') {
      top = actual === 'top'
        ? trigger.top - panel.height - gap
        : trigger.bottom + gap;
      top = clamp(top, margin, viewportH - panel.height - margin);
      left = clamp(centerX - panel.width / 2, margin, viewportW - panel.width - margin);
      arrowStyle = {
        left: clamp(centerX - left, 12, Math.max(12, panel.width - 12)),
        transform: 'translateX(-50%)',
        borderLeft: `${arrowSize}px solid transparent`,
        borderRight: `${arrowSize}px solid transparent`,
        ...(actual === 'top'
          ? { bottom: -arrowSize, borderTop: `${arrowSize}px solid rgba(255, 255, 255, 0.98)` }
          : { top: -arrowSize, borderBottom: `${arrowSize}px solid rgba(255, 255, 255, 0.98)` }),
      };
    } else {
      left = actual === 'left'
        ? trigger.left - panel.width - gap
        : trigger.right + gap;
      left = clamp(left, margin, viewportW - panel.width - margin);
      top = clamp(centerY - panel.height / 2, margin, viewportH - panel.height - margin);
      arrowStyle = {
        top: clamp(centerY - top, 12, Math.max(12, panel.height - 12)),
        transform: 'translateY(-50%)',
        borderTop: `${arrowSize}px solid transparent`,
        borderBottom: `${arrowSize}px solid transparent`,
        ...(actual === 'left'
          ? { right: -arrowSize, borderLeft: `${arrowSize}px solid rgba(255, 255, 255, 0.98)` }
          : { left: -arrowSize, borderRight: `${arrowSize}px solid rgba(255, 255, 255, 0.98)` }),
      };
    }

    setPosition({ top, left, placement: actual, arrowStyle });
  }, [placement]);

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => () => {
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);
    if (touchTimerRef.current !== null) window.clearTimeout(touchTimerRef.current);
  }, []);

  const showTooltip = useCallback(() => {
    if (hoverTimerRef.current !== null) return;
    hoverTimerRef.current = window.setTimeout(() => {
      hoverTimerRef.current = null;
      setPosition(null);
      setOpen(true);
    }, 120);
  }, []);

  const hideTooltip = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (touchTimerRef.current !== null) {
      window.clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
    setOpen(false);
  }, []);

  const setTriggerNode = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node;
    const childRef = (children as any).ref;
    if (typeof childRef === 'function') childRef(node);
    else if (childRef && typeof childRef === 'object') childRef.current = node;
  }, [children]);

  if (!label && !title && !detail && !meta) return children;

  const childProps = children.props || {};
  const trigger = React.cloneElement(children, {
    ref: setTriggerNode,
    style: { ...childProps.style, cursor: childProps.style?.cursor || 'help' },
    tabIndex: childProps.tabIndex,
    'aria-describedby': open ? tooltipId : undefined,
    onMouseEnter: (event: React.MouseEvent) => {
      childProps.onMouseEnter?.(event);
      showTooltip();
    },
    onMouseLeave: (event: React.MouseEvent) => {
      childProps.onMouseLeave?.(event);
      hideTooltip();
    },
    onFocus: (event: React.FocusEvent) => {
      childProps.onFocus?.(event);
      showTooltip();
    },
    onBlur: (event: React.FocusEvent) => {
      childProps.onBlur?.(event);
      hideTooltip();
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      childProps.onKeyDown?.(event);
      if (event.key === 'Escape') hideTooltip();
    },
    onTouchStart: (event: React.TouchEvent) => {
      childProps.onTouchStart?.(event);
      showTooltip();
      if (touchTimerRef.current !== null) window.clearTimeout(touchTimerRef.current);
      touchTimerRef.current = window.setTimeout(() => {
        touchTimerRef.current = null;
        setOpen(false);
      }, 2600);
    },
  });

  return (
    <>
      {trigger}
      {open && ReactDOM.createPortal(
        <div
          ref={panelRef}
          id={tooltipId}
          role="tooltip"
          className="sr-report-tooltip"
          data-placement={position?.placement ?? placement}
          style={{
            position: 'fixed',
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            maxWidth: `min(${maxW}, calc(100vw - 24px))`,
            padding: '6px 8px',
            borderRadius: '6px',
            border: '1px solid rgba(203, 213, 225, 0.95)',
            background: 'rgba(255, 255, 255, 0.98)',
            color: '#334155',
            fontSize: '10px',
            lineHeight: 1.32,
            fontWeight: 500,
            letterSpacing: 0,
            boxShadow: '0 10px 22px rgba(15, 23, 42, 0.14), 0 1px 2px rgba(15, 23, 42, 0.08)',
            zIndex: 4000,
            pointerEvents: 'none',
            opacity: position ? 1 : 0,
            transform: position ? 'translateY(0) scale(1)' : 'translateY(1px) scale(0.99)',
            transformOrigin: position?.placement === 'bottom' ? '50% 0%' : position?.placement === 'left' ? '100% 50%' : position?.placement === 'right' ? '0% 50%' : '50% 100%',
            transition: 'opacity 90ms ease-out, transform 120ms cubic-bezier(0.2, 0, 0.2, 1)',
            backdropFilter: 'blur(6px)',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
          }}
        >
          {renderTooltipContent(title, detail, meta, label, tone)}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              filter: 'drop-shadow(0 1px 1px rgba(15, 23, 42, 0.10))',
              ...position?.arrowStyle,
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
};
